import { storeToRefs } from 'pinia';
import { InstanceAccountType } from '@sysarb/sysarb-types/service-base/enums';
import { useLoginStore } from '@/login/stores/useLoginStore';
import { usePlanhat } from '@/composables/usePlanhat';
import { useUmami } from '@/composables/useUmami';
import { buildEnduserExternalId } from '@/tracking/enduserExternalId';
import { shouldSuppressTracking } from '@/tracking/suppression';
import { TRACKING_EVENTS, type TrackingEvent } from '@/tracking/events';

type UseTrackingReturn = {
    init: () => void;
    identify: () => void;
    startSession: () => void;
    endSession: () => void;
    track: (event: TrackingEvent, info?: Record<string, unknown>) => void;
};

/**
 * The only tracking API call sites should use. Knows nothing about Planhat —
 * a future CSM-tool swap changes usePlanhat, not this file or its callers.
 *
 * Depends on Pinia store getters only (no useUser), so it is safe to call from
 * event handlers and computeds outside setup() — the same constraint the legacy
 * useStartdeliver respected.
 */
export function useTracking(): UseTrackingReturn {
    const planhat = usePlanhat();
    const umami = useUmami();
    const loginStore = useLoginStore();
    const { userData, instance, isSupport, isSimulating } = storeToRefs(loginStore);

    function suppressed(): boolean {
        return shouldSuppressTracking({
            isSupport: isSupport.value,
            isConsultant: userData.value?.loginUser?.accountType === InstanceAccountType.Consultant,
            isSimulating: isSimulating.value,
            email: userData.value?.loginUser?.Email,
        });
    }

    function init(): void {
        planhat.init();
        umami.init();
    }

    function identify(): void {
        if (suppressed()) return;

        const loginUser = userData.value?.loginUser;
        const subDomain = instance.value;
        if (loginUser?.Id == null || loginUser.Email == null || subDomain == null) return;

        const fullName = loginUser.FullName.trim();

        planhat.identify(buildEnduserExternalId(loginUser.Id, subDomain), {
            email: loginUser.Email.toLowerCase(),
            name: fullName !== '' ? fullName : undefined,
            companyExternalId: subDomain,
        });
    }

    function track(event: TrackingEvent, info?: Record<string, unknown>): void {
        const definition = TRACKING_EVENTS[event];
        // Umami is anonymous product analytics, so the Planhat suppression rules do not apply to it.
        umami.track(definition.action, info);

        if (suppressed()) return;

        planhat.track(definition.action, { weight: definition.weight, info });
    }

    function startSession(): void {
        umami.identify();
    }

    function endSession(): void {
        umami.reset();
    }

    return { init, identify, startSession, endSession, track };
}
